import React from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw, Brain, BarChart3, Shield, Activity } from 'lucide-react';
import { Badge } from '../ui/components';
import { cryptoApi } from '../services/cryptoApi';
import { recommendationsApi } from '../services/recommendationsApi';

const chipColors = { success: '#16a34a', error: '#dc2626', warning: '#d97706', default: 'var(--ds-text-secondary)' };
const ChipBadge = ({ label, color = 'default' }) => (
  <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, backgroundColor: `${chipColors[color]}18`, color: chipColors[color] }}>{label}</span>
);

const DashboardPage = () => {
  const { data: cryptoData = [], isLoading: cryptoLoading, error: cryptoError } = cryptoApi.endpoints.getAllCryptoCurrencies.useQuery();
  const { data: recommendationsData = [], isLoading: recommendationsLoading, error: recommendationsError } = recommendationsApi.endpoints.getAllLatestRecommendations.useQuery();
  const [refreshCryptoData] = cryptoApi.endpoints.refreshCryptoData.useMutation();

  const handleRefreshCrypto = async (id) => { try { await refreshCryptoData(id).unwrap(); } catch (e) { console.error('Failed to refresh:', e); } };

  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(v);
  const fmtCap = (v) => { if (v >= 1e12) return `$${(v/1e12).toFixed(2)}T`; if (v >= 1e9) return `$${(v/1e9).toFixed(2)}B`; if (v >= 1e6) return `$${(v/1e6).toFixed(2)}M`; return fmt(v); };

  const PriceIcon = ({ change }) => {
    if (change > 0) return <TrendingUp size={18} style={{ color: '#16a34a' }} />;
    if (change < 0) return <TrendingDown size={18} style={{ color: '#dc2626' }} />;
    return <Minus size={18} style={{ color: 'var(--ds-text-tertiary)' }} />;
  };
  const priceColor = (c) => c > 0 ? '#16a34a' : c < 0 ? '#dc2626' : 'var(--ds-text-secondary)';

  const levelColor = (l) => { const m = { low: 'success', medium: 'warning', high: 'error', bullish: 'success', bearish: 'error', neutral: 'warning', sideways: 'warning' }; return m[l?.toLowerCase()] || 'default'; };

  const recommendationsMap = recommendationsData.reduce((a, r) => { a[r.cryptoCurrencyId] = r; return a; }, {});

  if (cryptoLoading || recommendationsLoading) {
    return (
      <div style={{ maxWidth: 1200, margin: '32px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--ds-border-default)', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ width: '60%', height: '100%', backgroundColor: 'var(--ds-primary)', animation: 'pulse 1.5s infinite' }} />
        </div>
        <p style={{ fontSize: '1rem', color: 'var(--ds-text-secondary)' }}>Loading dashboard data...</p>
      </div>
    );
  }

  if (cryptoError || recommendationsError) {
    return (
      <div style={{ maxWidth: 1200, margin: '32px auto', padding: '0 24px' }}>
        <div style={{ padding: '12px 16px', borderRadius: 'var(--ds-border-radius-medium)', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626' }}>
          Failed to load dashboard data. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '32px auto', padding: '0 24px' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--ds-text-primary)' }}>Crypto Trading Dashboard</h1>
        <p style={{ fontSize: '1rem', color: 'var(--ds-text-secondary)' }}>AI-powered cryptocurrency analysis and recommendations</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360, 1fr))', gap: 24 }}>
        {cryptoData.map((crypto) => {
          const rec = recommendationsMap[crypto.cryptoCurrencyId];
          return (
            <div key={crypto.cryptoCurrencyId} style={{ border: '1px solid var(--ds-border-default)', borderRadius: 'var(--ds-border-radius-large)', backgroundColor: 'var(--ds-bg-primary)', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', padding: 16, gap: 12, borderBottom: '1px solid var(--ds-border-default)' }}>
                {crypto.imageUrl ? (
                  <img src={crypto.imageUrl} alt={crypto.symbol} style={{ width: 40, height: 40, borderRadius: '50%' }} />
                ) : (
                  <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: 'var(--ds-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>{crypto.symbol?.charAt(0)}</div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: 'var(--ds-text-primary)' }}>{crypto.name}</span>
                    <ChipBadge label={crypto.symbol} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--ds-text-tertiary)' }}>Rank #{crypto.marketCapRank || 'N/A'}</span>
                </div>
                <button onClick={() => handleRefreshCrypto(crypto.cryptoCurrencyId)} title="Refresh Data" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 'var(--ds-border-radius-medium)', color: 'var(--ds-text-secondary)' }}>
                  <RefreshCw size={16} />
                </button>
              </div>

              {/* Content */}
              <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Price */}
                <div style={{ padding: 12, backgroundColor: 'var(--ds-bg-secondary)', borderRadius: 'var(--ds-border-radius-medium)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ds-text-primary)' }}>{fmt(crypto.currentPrice)}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <PriceIcon change={crypto.priceChangePercentage24h} />
                      <span style={{ fontSize: '0.875rem', fontWeight: 500, color: priceColor(crypto.priceChangePercentage24h) }}>{crypto.priceChangePercentage24h?.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[['Market Cap', fmtCap(crypto.marketCap)], ['24h Volume', fmtCap(crypto.totalVolume)], ['24h High', crypto.high24h ? fmt(crypto.high24h) : 'N/A'], ['24h Low', crypto.low24h ? fmt(crypto.low24h) : 'N/A']].map(([l, v]) => (
                      <div key={l}><span style={{ fontSize: '0.7rem', color: 'var(--ds-text-tertiary)' }}>{l}</span><div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--ds-text-primary)' }}>{v}</div></div>
                    ))}
                  </div>
                </div>

                {/* AI */}
                {rec ? (
                  <div style={{ padding: 12, backgroundColor: 'var(--ds-bg-secondary)', borderRadius: 'var(--ds-border-radius-medium)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <Brain size={18} style={{ color: 'var(--ds-primary)' }} />
                      <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--ds-text-primary)' }}>AI Analysis</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--ds-text-tertiary)' }}>{new Date(rec.dateGenerated).toLocaleDateString()}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {rec.trendAnalysis && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>Trend</span><ChipBadge label={rec.trendAnalysis.trend} color={levelColor(rec.trendAnalysis.trend)} /></div>}
                      {rec.riskAssessment && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>Risk Level</span><ChipBadge label={rec.riskAssessment.level} color={levelColor(rec.riskAssessment.level)} /></div>}
                      {rec.sentimentAnalysis && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>Sentiment</span><ChipBadge label={rec.sentimentAnalysis.level} color={levelColor(rec.sentimentAnalysis.level)} /></div>}
                      {rec.volatilityAnalysis && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>Volatility</span><ChipBadge label={rec.volatilityAnalysis.level} color={levelColor(rec.volatilityAnalysis.level)} /></div>}
                      {rec.technicalIndicators && (
                        <div><span style={{ fontSize: '0.7rem', color: 'var(--ds-text-tertiary)' }}>Technical Indicators</span>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 4 }}>
                            <span style={{ fontSize: '0.875rem' }}>RSI: {rec.technicalIndicators.rsi?.toFixed(2) || 'N/A'}</span>
                            <span style={{ fontSize: '0.875rem' }}>MACD: {rec.technicalIndicators.macd?.toFixed(2) || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                      {rec.pricePredictions && (
                        <div><span style={{ fontSize: '0.7rem', color: 'var(--ds-text-tertiary)' }}>Price Predictions</span>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 4 }}>
                            <span style={{ fontSize: '0.75rem' }}>1W: {rec.pricePredictions.oneWeek ? fmt(rec.pricePredictions.oneWeek) : 'N/A'}</span>
                            <span style={{ fontSize: '0.75rem' }}>1M: {rec.pricePredictions.oneMonth ? fmt(rec.pricePredictions.oneMonth) : 'N/A'}</span>
                            <span style={{ fontSize: '0.75rem' }}>3M: {rec.pricePredictions.threeMonths ? fmt(rec.pricePredictions.threeMonths) : 'N/A'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 12, backgroundColor: 'var(--ds-bg-secondary)', borderRadius: 'var(--ds-border-radius-medium)', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>No AI recommendations available</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {cryptoData.length === 0 && (
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <p style={{ fontSize: '1rem', color: 'var(--ds-text-secondary)' }}>No cryptocurrency data available</p>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
